// Functions
import formatErrors from '../../../functions/formatErrors';
// Types
import { MarginState } from '../objects/marginState';
import {
    ADD_PREFERENCES,
    ADD_RATE,
    DELETE_RATE,
    MarginActionTypes,
    MOVE_DOWN_COLUMN,
    MOVE_UP_COLUMN,
    RESET_FILTERS,
    RESET_MARGIN,
    SET_CONTENT_TYPE_ERROR,
    SET_ERRORS,
    SET_EXPORT_TYPE,
    SET_LIMIT,
    SET_LIST,
    SET_MARGIN,
    SET_ORDER_BY,
    SET_PAGE,
    SET_PREFERENCES as SET_MARGIN_PREFERENCES,
    SET_RUNNING,
    SET_SEARCH,
    SET_SUSPEND,
    CANCEL_SUSPEND,
    TOGGLE_COLUMN,
    TOGGLE_CREATE,
    TOGGLE_EXPORT,
    TOGGLE_EXPORT_FULL,
    TOGGLE_FILTERS,
    TOGGLE_NAME_DEPLOYED,
    TOGGLE_REFRESH,
    TOGGLE_SURCOM,
    TOGGLE_TVA_MARGIN_ONLY,
    UPDATE_APPLIED_UNDER,
    UPDATE_CONTENT_TYPE,
    UPDATE_CURRENCY,
    UPDATE_DATE_GTE,
    UPDATE_DATE_LTE,
    UPDATE_DESTINATIONS,
    UPDATE_FILTER_ACTIVATED,
    UPDATE_FILTER_CONTENT_TYPE,
    UPDATE_FILTER_CREATED_DATE_GTE,
    UPDATE_FILTER_CREATED_DATE_LTE,
    UPDATE_FILTER_CREATOR,
    UPDATE_FILTER_LAST_AUTHOR,
    UPDATE_FILTER_MODIFIED_DATE_GTE,
    UPDATE_FILTER_MODIFIED_DATE_LTE,
    UPDATE_FILTER_NETWORK,
    UPDATE_FILTER_ORGANISATION,
    UPDATE_FILTER_PERIOD,
    UPDATE_FILTER_PERIOD_TYPE,
    UPDATE_FLIGHT_CLASS_BOOKING,
    UPDATE_FLIGHT_FARE_BASIS,
    UPDATE_FLIGHT_IATA_AIRLINE,
    UPDATE_FLIGHT_SPECIFICS,
    UPDATE_NAME,
    UPDATE_NAME_LOCALE,
    UPDATE_NETWORK,
    UPDATE_ORGANISATION,
    UPDATE_PROVIDER,
    UPDATE_RATE,
    UPDATE_RATE_TYPE,
    UPDATE_TARGET,
    UPDATE_TYPE,
    SET_COLUMNS,
    SET_COMPANY,
    SET_COMPANIES,
    SET_NAME_LOCALES,
    UPDATE_BOOKING_ENGINES,
    UPDATE_FILTER_BOOKING_ENGINES,
    UPDATE_CMS,
    UPDATE_FUNNELS,
    UPDATE_MARKETS,
    UPDATE_FLIGHT_SPECIFICS_LEVEL,
    ADD_FLIGHT_SPECIFICS_LEVEL,
    DELETE_FLIGHT_SPECIFICS_LEVEL,
    UPDATE_IS_MARKUP,
    SET_EDIT_MARGIN,
    RESIZE_COLUMN
} from './actionTypes';
import { ProfileActionTypes, SET_PREFERENCES } from '../../profile/redux/actionTypes';
import { EMPTY_FLIGHT_SPECIFIC_LEVEL } from '../objects/flightSpecificLevel';
import { DEFAULT_COLUMN } from '../constants/marginConstants';
import { applyColumnResize } from '../../utils/list/utils/columnResize';
import { setMarginPreference } from '../utils/setMarginPreference';

const initialState: MarginState = {
    company: null,
    companies: [],
    columns: DEFAULT_COLUMN,
    content_type: [],
    content_type_error: '',
    count: 0,
    create: false,
    created_date: null,
    creator: null,
    currency: null,
    date_gte: null,
    date_lte: null,
    delete_margin: null,
    destinations: [],
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    filter_activated: 'ACTIVATED',
    filter_content_type: [],
    filter_booking_engines: [],
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_network: null,
    filter_open: false,
    filter_organisation: null,
    filter_period: 'CREATION',
    filter_period_type: 0,
    flight_class_booking: '',
    flight_fare_basis: '',
    flight_iata_airline: null,
    flight_specifics: 0,
    flight_specifics_levels: [],
    id: null,
    is_surcom: false,
    last_author: null,
    limit: 50,
    list: null,
    margin: null,
    modified_date: null,
    name: '',
    name_deployed: false,
    name_error: '',
    date_gte_error: '',
    date_lte_error: '',
    name_locales: {},
    network: null,
    order: 'asc',
    order_by: 'name',
    organisation: null,
    page: 0,
    preferences: [],
    provider: null,
    rates: [{
        applied_under: null,
        currency: null,
        rate: 0,
        rate_type: 'FIX'
    }],
    refresh: false,
    running: false,
    search: '',
    suspend: null,
    target: 0,
    type: 0,
    tva_margin_only: false,
    booking_engines: [],
    cms: [],
    funnels: [],
    markets: [],
    is_markup: false,
    editMargin: null
};

export default (state: MarginState = initialState, action: MarginActionTypes | ProfileActionTypes): MarginState => {
    switch (action.type) {
        case ADD_PREFERENCES: {
            return { ...state, preferences: [...state.preferences, ...action.payload] };
        }
        case ADD_RATE: {
            const rates = [...state.rates];
            const old_rate = (rates.length > 0) ? rates[rates.length - 1] : null;
            rates.push({
                applied_under: 0,
                currency: null,
                rate: 0,
                rate_type: old_rate?.rate_type || 'FIX'
            });
            return { ...state, rates: [...rates] };
        }
        case DELETE_RATE: {
            const rates = [...state.rates];
            rates.splice(action.payload, 1);
            return { ...state, rates: [...rates] };
        }
        case MOVE_DOWN_COLUMN: {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload + 1] };
            columns[action.payload + 1] = columns[action.payload];
            columns[action.payload + 1].last_updated = true;
            columns[action.payload] = column;
            return { ...state, columns: columns };
        }
        case MOVE_UP_COLUMN: {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload - 1] };
            columns[action.payload - 1] = columns[action.payload];
            columns[action.payload - 1].last_updated = true;
            columns[action.payload] = column;
            return { ...state, columns: columns };
        }
        case RESET_FILTERS: {
            return {
                ...state,
                filter_activated: 'ACTIVATED',
                filter_content_type: [],
                filter_booking_engines: [],
                filter_created_date_gte: null,
                filter_created_date_lte: null,
                filter_creator: '',
                filter_last_author: '',
                filter_modified_date_gte: null,
                filter_modified_date_lte: null,
                filter_network: null
            };
        }
        case RESET_MARGIN: {
            return {
                ...state,
                create: false,
                content_type: [],
                companies: [],
                currency: null,
                date_gte: null,
                date_lte: null,
                destinations: [],
                id: null,
                is_surcom: false,
                flight_specifics: 0,
                flight_specifics_levels: [],
                margin: null,
                name: '',
                name_deployed: false,
                name_error: '',
                date_gte_error: '',
                date_lte_error: '',
                name_locales: {},
                network: null,
                organisation: null,
                provider: null,
                rates: [{
                    applied_under: null,
                    currency: null,
                    rate: 0,
                    rate_type: 'FIX'
                }],
                target: 0,
                tva_margin_only: false,
                type: 0,
                booking_engines: [],
                cms: [],
                funnels: [],
                markets: [],
                is_markup: false
            };
        }
        case SET_ERRORS: {
            const { name, date_gte, date_lte } = action.payload;
            return {
                ...state,
                name_error: name ? Array.isArray(name) ? formatErrors(name) : name : '',
                date_gte_error: date_gte || '',
                date_lte_error: date_lte || '',
            };
        }
        case SET_CONTENT_TYPE_ERROR: {
            return { ...state, content_type_error: action.payload };
        }
        case SET_EXPORT_TYPE: {
            return { ...state, export_type: action.payload };
        }
        case SET_LIMIT: {
            return { ...state, list: null, limit: action.payload };
        }
        case SET_LIST: {
            const { count, results } = action.payload;
            return { ...state, count: count, list: results, running: false };
        }
        case SET_ORDER_BY: {
            return { ...state, list: null, order_by: action.payload, order: state.order_by === action.payload && state.order === 'asc' ? 'desc' : 'asc' };
        }
        case SET_PAGE: {
            return { ...state, list: null, page: action.payload };
        }
        case SET_PREFERENCES: {
            const { columns, preferences, order_by, order } = setMarginPreference(action.payload, state.columns);
            return { ...state, columns, preferences, order_by, order };
        }
        case RESIZE_COLUMN: {
            return { ...state, columns: applyColumnResize(state.columns, action.payload) };
        }
        case SET_MARGIN: {
            const { name, localization } = action.payload;
            const localized_name: any & { [index: number]: any } = {};
            localization.map(locale => {
                localized_name[Number(locale.locale)] = locale.name;
            });
            return { ...state, margin: action.payload, name: name, name_locales: localized_name, companies: action.payload.client_branch };
        }
        case SET_MARGIN_PREFERENCES: {
            return { ...state, preferences: action.payload };
        }
        case SET_RUNNING: {
            return { ...state, running: action.payload };
        }
        case SET_SEARCH: {
            return { ...state, list: null, page: 0, search: action.payload };
        }
        case SET_SUSPEND: {
            if(action.payload === false) {
                return { ...state, suspend: null };
            }
            if (action.payload === null && state.suspend !== null && state.list !== null) {
                const list = [...state.list];
                for (let i = 0; i < list.length; i++) {
                    if (list[i].id === state.suspend.id) {
                        list.splice(i, 1);
                        return { ...state, suspend: null, list: list, count: state.count - 1 };
                    }
                }
            }
            return { ...state, suspend: action.payload };
        }
        case CANCEL_SUSPEND: {
            return { ...state, suspend: null };
        }
        case TOGGLE_COLUMN: {
            const columns = [...state.columns];
            const column = { ...columns[action.payload] };
            column.checked = !column.checked;
            columns[action.payload] = column;
            return { ...state, columns: columns };
        }
        case TOGGLE_CREATE: {
            return { ...state, create: !state.create };
        }
        case TOGGLE_EXPORT: {
            return { ...state, export_list: action.payload };
        }
        case TOGGLE_EXPORT_FULL: {
            return { ...state, export_full: !state.export_full };
        }
        case TOGGLE_FILTERS: {
            return { ...state, filter_open: !state.filter_open };
        }
        case TOGGLE_NAME_DEPLOYED: {
            return { ...state, name_deployed: action.payload };
        }
        case TOGGLE_REFRESH: {
            return { ...state, refresh: !state.refresh };
        }
        case TOGGLE_SURCOM: {
            return { ...state, is_surcom: !state.is_surcom };
        }
        case TOGGLE_TVA_MARGIN_ONLY: {
            return { ...state, tva_margin_only: !state.tva_margin_only };
        }
        case UPDATE_APPLIED_UNDER: {
            const { value, index } = action.payload;
            const rates = [...state.rates];
            rates[index] = { ...rates[index] };
            rates[index].applied_under = value;
            return { ...state, rates: [...rates] };
        }
        case UPDATE_CONTENT_TYPE: {
            return { ...state, content_type: action.payload };
        }
        case UPDATE_CURRENCY: {
            const { value, index } = action.payload;
            const rates = [...state.rates];
            rates[index] = { ...rates[index] };
            rates[index].currency = value;
            return { ...state, rates: [...rates] };
        }
        case UPDATE_DESTINATIONS: {
            return { ...state, destinations: action.payload };
        }
        case UPDATE_FILTER_ACTIVATED: {
            return { ...state, filter_activated: action.payload };
        }
        case UPDATE_FILTER_CONTENT_TYPE: {
            return { ...state, filter_content_type: action.payload };
        }
        case UPDATE_FILTER_BOOKING_ENGINES: {
            return { ...state, filter_booking_engines: action.payload };
        }
        case UPDATE_FILTER_CREATED_DATE_GTE: {
            return { ...state, filter_created_date_gte: action.payload };
        }
        case UPDATE_FILTER_CREATED_DATE_LTE: {
            return { ...state, filter_created_date_lte: action.payload };
        }
        case UPDATE_FILTER_CREATOR: {
            return { ...state, filter_creator: action.payload };
        }
        case UPDATE_FILTER_LAST_AUTHOR: {
            return { ...state, filter_last_author: action.payload };
        }
        case UPDATE_FILTER_MODIFIED_DATE_GTE: {
            return { ...state, filter_modified_date_gte: action.payload };
        }
        case UPDATE_FILTER_MODIFIED_DATE_LTE: {
            return { ...state, filter_modified_date_lte: action.payload };
        }
        case UPDATE_FILTER_NETWORK: {
            return { ...state, filter_network: action.payload };
        }
        case UPDATE_FILTER_ORGANISATION: {
            return { ...state, filter_organisation: action.payload };
        }
        case UPDATE_FILTER_PERIOD: {
            const period = action.payload;
            return {
                ...state,
                filter_period: period,
                filter_period_type: 0,
                filter_created_date_gte: period === 'MODIFICATION' ? null : state.filter_created_date_gte,
                filter_created_date_lte: period === 'MODIFICATION' ? null : state.filter_created_date_lte,
                filter_modified_date_gte: period === 'CREATION' ? null : state.filter_modified_date_gte,
                filter_modified_date_lte: period === 'CREATION' ? null : state.filter_modified_date_lte,
            };
        }
        case UPDATE_FILTER_PERIOD_TYPE: {
            return { ...state, filter_period_type: action.payload };
        }
        case UPDATE_FLIGHT_CLASS_BOOKING: {
            return { ...state, flight_class_booking: action.payload };
        }
        case UPDATE_FLIGHT_FARE_BASIS: {
            return { ...state, flight_fare_basis: action.payload };
        }
        case UPDATE_FLIGHT_IATA_AIRLINE: {
            return { ...state, flight_iata_airline: action.payload };
        }
        case UPDATE_FLIGHT_SPECIFICS: {
            return { ...state, flight_specifics: action.payload, flight_class_booking: '', flight_fare_basis: '', flight_iata_airline: null };
        }
        case UPDATE_DATE_GTE: {
            return { ...state, date_gte: action.payload };
        }
        case UPDATE_DATE_LTE: {
            return { ...state, date_lte: action.payload };
        }
        case UPDATE_NAME: {
            return { ...state, name: action.payload };
        }
        case UPDATE_NAME_LOCALE: {
            const { id, value } = action.payload;
            const name_locales = { ...state.name_locales };
            name_locales[id] = value;
            return { ...state, name_locales: name_locales };
        }
        case UPDATE_NETWORK: {
            return { ...state, network: action.payload };
        }
        case UPDATE_ORGANISATION: {
            return { ...state, organisation: action.payload };
        }
        case UPDATE_PROVIDER: {
            return { ...state, provider: action.payload, content_type: [] };
        }
        case UPDATE_RATE: {
            const { value, index } = action.payload;
            const rates = [...state.rates];
            rates[index] = { ...rates[index] };
            rates[index].rate = value;
            return { ...state, rates: [...rates] };
        }
        case UPDATE_RATE_TYPE: {
            const { value, index } = action.payload;
            const rates = [...state.rates];
            rates[index] = { ...rates[index] };
            rates[index].rate_type = value;
            rates[index].currency = null;
            return { ...state, rates: [...rates] };
        }
        case UPDATE_TARGET: {
            return {
                ...state,
                target: action.payload,
                network: null,
                organisation: null,
                is_surcom: false,
                booking_engines: [],
                cms: [],
                funnels: [],
                markets: [],
                is_markup: false,
                flight_class_booking: '',
                flight_iata_airline: null,
                flight_specifics: 0,
                flight_fare_basis: '',
                flight_specifics_levels: [],
            };
        }
        case UPDATE_TYPE: {
            return { ...state, type: action.payload, provider: null, content_type: [], destinations: [], tva_margin_only: false };
        }
        case SET_COLUMNS: {
            return { ...state, columns: action.payload };
        }
        case SET_COMPANY: {
            return { ...state, company: action.payload };
        }
        case SET_COMPANIES: {
            return { ...state, companies: action.payload };
        }
        case SET_NAME_LOCALES: {
            return { ...state, name_locales: action.payload };
        }
        case UPDATE_BOOKING_ENGINES: {
            return { ...state, booking_engines: action.payload };
        }
        case UPDATE_CMS: {
            return { ...state, cms: action.payload };
        }
        case UPDATE_FUNNELS: {
            return { ...state, funnels: action.payload };
        }
        case UPDATE_MARKETS: {
            return { ...state, markets: action.payload };
        }
        case UPDATE_FLIGHT_SPECIFICS_LEVEL: {
            return { ...state, flight_specifics_levels: state.flight_specifics_levels.map((level, index) => index === action.payload.index ? action.payload.value : level) };
        }
        case ADD_FLIGHT_SPECIFICS_LEVEL: {
            return { ...state, flight_specifics_levels: [...state.flight_specifics_levels, EMPTY_FLIGHT_SPECIFIC_LEVEL] };
        }
        case DELETE_FLIGHT_SPECIFICS_LEVEL: {
            return { ...state, flight_specifics_levels: state.flight_specifics_levels.filter((level, index) => index !== action.payload) };
        }
        case UPDATE_IS_MARKUP: {
            return { ...state, is_markup: action.payload };
        }
        case SET_EDIT_MARGIN: {
            return { ...state, editMargin: action.payload };
        }
        default:
            return state;
    }
};